import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadSimple, CircleNotch, CheckCircle } from "@phosphor-icons/react";

export function AssayUploadDialog({ pid, areas, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [area, setArea] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const aid = area || (areas[0]?.area_id || "");

  const upload = async () => {
    if (!file || !aid) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/projects/${pid}/areas/${aid}/boreholes/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.count} sample(s) uploaded`);
      onUploaded && onUploaded();
    } catch (e) { toast.error(apiError(e)); setResult({ error: apiError(e) }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); setFile(null); } }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8" data-testid="assay-upload-open">
          <UploadSimple size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Upload Assays</span>
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="assay-upload-dialog">
        <DialogHeader><DialogTitle>Upload Assay / Soil-Geochem Data</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-muted-foreground">
          Upload a CSV with <span className="font-mono text-foreground">sample_id, lon, lat</span> (WGS84) and one or more grade columns (e.g. <span className="font-mono">Au_ppm</span>, <span className="font-mono">Cu_ppm</span>). Samples plot on the geochemistry map coloured by grade, with the distance to the nearest exploration target.
        </DialogDescription>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Licence area</label>
            <select data-testid="assay-upload-area" value={aid} onChange={(e) => setArea(e.target.value)}
              className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:border-[#E67332]">
              {areas.map((a) => <option key={a.area_id} value={a.area_id}>{a.name || a.area_id}</option>)}
            </select>
          </div>
          <input type="file" accept=".csv,text/csv" data-testid="assay-upload-file" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:text-xs file:font-medium file:cursor-pointer" />
          {result && !result.error && (
            <div className="rounded-md border border-[#10B981]/40 bg-[#10B981]/10 p-3 text-xs" data-testid="assay-upload-result">
              <div className="flex items-center gap-1.5 text-[#10B981] font-medium"><CheckCircle size={14} /> {result.count} sample(s) loaded{result.grade_fields?.length ? ` · grades: ${result.grade_fields.join(", ")}` : ""}</div>
              {result.issues?.length ? <div className="text-muted-foreground mt-1">{result.issues.length} warning(s) - check the CSV formatting.</div> : null}
            </div>
          )}
          <Button onClick={upload} disabled={busy || !file} data-testid="assay-upload-submit">
            {busy ? <><CircleNotch size={15} className="animate-spin mr-1.5" /> Uploading…</> : "Upload samples"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
